import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { drumRows, type DrumRow } from "../services/drum-map";
import {
  MetalTeachingKit,
  type BackingInstrument,
} from "../services/metal-teaching-kit";
import type { Tempo } from "../types";

type Pattern = Record<DrumRow, number[]>;
type Preset = { id: string; label: string; explanation: string };
type BackingChoice = {
  id: string;
  label: string;
  description: string;
  worksWith: string;
};

const rows = drumRows;
const STEPS_PER_BAR = 16;
const BAR_COUNT = 4;
const STEP_COUNT = STEPS_PER_BAR * BAR_COUNT;
const PHRASE_STEPS = 32;
const steps = () => Array<number>(STEP_COUNT).fill(0);
// The teaching patterns begin as two-bar ideas and repeat into a four-bar
// phrase. Every one of the 64 cells remains independently editable.
const hits = (indexes: number[], level = 0.72) => Array.from(
  { length: STEP_COUNT },
  (_, index) => indexes.includes(index % PHRASE_STEPS) ? level : 0,
);
const strengthClass = (level: number) => level === 0 ? "" : level < 0.5 ? "hit soft-hit" : level < 0.9 ? "hit normal-hit" : "hit strong-hit";
const basePattern = (): Pattern => ({
  kick: hits([0, 6, 16, 22], 0.9),
  snare: hits([4, 12, 20, 28], 0.9),
  hiHat: Array.from({ length: STEP_COUNT }, (_, index) => index % 2 === 0 ? (index % 4 === 0 ? 0.85 : 0.55) : 0),
  ride: steps(),
  crash: steps(),
  rackTom: steps(),
  floorTom: steps(),
});
const clonePattern = (pattern: Pattern): Pattern => ({
  kick: [...pattern.kick],
  snare: [...pattern.snare],
  hiHat: [...pattern.hiHat],
  ride: [...pattern.ride],
  crash: [...pattern.crash],
  rackTom: [...pattern.rackTom],
  floorTom: [...pattern.floorTom],
});
const unmutedRows = (): Record<DrumRow, boolean> => ({
  kick: false,
  snare: false,
  hiHat: false,
  ride: false,
  crash: false,
  rackTom: false,
  floorTom: false,
});
const resetCopy: Record<string, string> = {
  "build-a-groove": "Start with a basic backbeat, then mute one row or change individual cells to hear what each piece contributes.",
  "change-the-weight": "The tempo stays fixed. Choose a pattern to hear how moving the snare or changing the kick changes how heavy it feels.",
  "detail-and-motion": "Start with a plain groove. Ghost notes, accents, and fills are additive: each button changes the groove you currently have.",
  "metal-engines": "Choose one pattern at a time. Watch which hands and feet strike together and which alternate—the grid is the explanation.",
};
const backingChoices: BackingChoice[] = [
  {
    id: "",
    label: "Drums only",
    description: "Hear every drum decision without a backing track.",
    worksWith: "Any groove",
  },
  {
    id: "iron-chug",
    label: "Iron Chug",
    description: "Low-string stops and chromatic power-chord punches.",
    worksWith: "Backbeats, half-time, fills, and kicks that follow the riff",
  },
  {
    id: "gallop-drive",
    label: "Gallop Drive",
    description: "A long–short–short charge with a four-bar turnaround.",
    worksWith: "Gallops, double kick, D-beats, and skank beats",
  },
  {
    id: "tremolo-storm",
    label: "Tremolo Storm",
    description: "Continuous alternate-picked chords with moving roots.",
    worksWith: "Traditional, hammer, bomb, and Northern Hyperblasts",
  },
  {
    id: "faultline",
    label: "Faultline",
    description: "Syncopated low-string hits, a tense tritone, and abrupt rests.",
    worksWith: "Breakdowns, odd groupings, and sparse heavy grooves",
  },
];
const defaultBackingLevels: Record<BackingInstrument, number> = {
  guitar: -1.5,
  bass: 0.5,
};
const defaultDrumLevels = (): Record<DrumRow, number> => ({
  // CrocellKit's kick carries plenty of sub energy, but its attack is masked
  // by distorted guitars and bright cymbals. This teaching offset makes the
  // foot pattern readable while the browser master limiter retains headroom.
  kick: 4,
  snare: 0,
  hiHat: 0,
  ride: 0,
  crash: 0,
  rackTom: 0,
  floorTom: 0,
});

const presets: Record<string, Preset[]> = {
  "build-a-groove": [
    { id: "backbeat", label: "Basic backbeat", explanation: "The hi-hat hits twice per beat—these are eighth notes. The snare marks beats 2 and 4. The kick gives the riff a low foundation." },
    { id: "kick-answer", label: "Kick answers", explanation: "Extra kick notes answer the spaces between snare hits. This is the beginning of writing a drum part around a riff." },
  ],
  "change-the-weight": [
    { id: "half-time", label: "Half-time", explanation: "The snare moves to beat 3. Nothing gets slower, but the wider space makes the groove feel heavier." },
    { id: "double-time", label: "Double-time", explanation: "The snare arrives twice as often. The tempo is unchanged, but the groove feels urgent." },
    { id: "gallop", label: "Gallop", explanation: "The kick repeats a long–short–short shape, one of metal's most recognizable foot patterns." },
    { id: "double-kick", label: "Double kick", explanation: "Both feet alternate to play four kick hits per beat. Those four equal divisions are called sixteenth notes." },
    { id: "riff-sync", label: "Follow the riff", explanation: "The kick copies an uneven accent shape. This is riff-synchronous drumming: the feet underline the guitar rhythm." },
    { id: "space", label: "Leave space", explanation: "Only a few large crash, kick, and snare hits remain. This is pocket and weight: placement matters more than note count." },
  ],
  "detail-and-motion": [
    { id: "ghosts", label: "Add ghost notes", explanation: "The quiet in-between notes are on the snare, their traditional home. They add motion without replacing the main backbeat." },
    { id: "accents", label: "Shape cymbal accents", explanation: "Crash hits mark each beat while softer hi-hat notes connect them. Drummers can accent other pieces too, but the crash is the traditional large punctuation mark." },
    { id: "fill", label: "Add fill", explanation: "The last beat moves across the toms, while the groove before it stays exactly as you built it." },
    { id: "swing", label: "Swing the pulse", explanation: "The ride alternates long then short instead of evenly spaced. That lilt is the swing heard in early metal and many fill-heavy styles." },
  ],
  "metal-engines": [
    { id: "d-beat", label: "D-beat", explanation: "A punk pattern: the kick lands before and after the snare, making the groove feel as if it is always tipping forward." },
    { id: "skank", label: "Skank beat", explanation: "Kick and snare alternate quickly under a steady hi-hat. Thrash and early extreme metal use it for forward motion." },
    { id: "traditional-blast", label: "Traditional blast", explanation: "Kick plus hi-hat alternate with snare. The rapid back-and-forth makes the separate hits sound like a blur." },
    { id: "hammer-blast", label: "Hammer blast", explanation: "Kick, snare, and hi-hat hit together instead of alternating. Watch the three rows light up in the same columns." },
    { id: "bomb-blast", label: "Bomb blast", explanation: "The kick runs continuously beneath the hand pattern, making the entire beat feel more relentless." },
    { id: "hyperblast", label: "Northern Hyperblast", explanation: "The snare keeps hitting rapidly while kick and ride mark a much slower beat." },
    { id: "breakdown", label: "Breakdown", explanation: "Sparse kick-and-crash accents lock to the riff. Empty space makes every shared hit feel larger." },
    { id: "odd-grouping", label: "Odd grouping", explanation: "The kick repeats every three grid cells while the snare stays on beats 2 and 4. The riff shifts; the backbeat keeps your place." },
  ],
};

@customElement("groove-lab")
export class GrooveLab extends LitElement {
  @property({ type: String }) concept = "build-a-groove";
  @property({ attribute: false }) kitManifest = "";
  @property({ attribute: false }) backingManifest = "";
  @state() private pattern = basePattern();
  @state() private playing = false;
  @state() private kitLoading = false;
  @state() private audioError = "";
  @state() private step = -1;
  @state() private tempo = 1;
  @state() private selectedPreset = "backbeat";
  @state() private explanation = presets["build-a-groove"][0].explanation;
  @state() private muted: Record<DrumRow, boolean> = unmutedRows();
  @state() private swing = false;
  @state() private selectedBacking = "iron-chug";
  @state() private backingEnabled: Record<BackingInstrument, boolean> = {
    guitar: true,
    bass: true,
  };
  @state() private backingLevels = { ...defaultBackingLevels };
  @state() private drumLevels = defaultDrumLevels();
  private timer?: number;
  private nextStepAtMs?: number;
  private metalKit = new MetalTeachingKit();

  static styles = css`
    :host{display:block}.toolbar{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px}.toolbar>button,.tempo button{border:1px solid #3a3a3e;background:#19191b;color:#ddd;border-radius:8px;padding:10px 12px;cursor:pointer;font:700 11px/1 sans-serif}.toolbar>button:hover,.toolbar>button:focus-visible,.tempo button:hover{border-color:#d7ff3f;outline:none}.toolbar>button:disabled{cursor:wait;opacity:.65}.toolbar .primary{background:#d7ff3f;color:#0a0a0b;border-color:#d7ff3f;min-width:90px}.toolbar button.active{background:#e8e8df;color:#111}.toolbar button.additive{border-color:#8c77ff}.tempo{display:flex;gap:5px;margin-left:auto}.kit-note{display:flex;gap:7px;align-items:center;margin:0 0 15px;color:#8f8f94;font:700 10px/1.4 monospace;text-transform:uppercase}.kit-note::before{content:"";width:7px;height:7px;border-radius:50%;background:#d7ff3f;box-shadow:0 0 8px #d7ff3f}.kit-note.error{color:#ff725e}.kit-note.error::before{background:#ff725e;box-shadow:none}.kit-balance{margin:0 0 18px;padding:14px 16px;border:1px solid #303035;border-radius:10px;background:#151517}.kit-balance-head{display:flex;align-items:baseline;justify-content:space-between;gap:12px;margin-bottom:11px}.kit-balance-head h3{margin:0;color:#f2f2ed;font-size:14px}.kit-balance-head span{color:#8f8f94;font:700 9px/1 monospace;text-transform:uppercase}.piece-levels{display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:10px 15px}.piece-level{display:grid;grid-template-columns:62px minmax(75px,1fr) 38px 25px;align-items:center;gap:7px}.piece-level label{color:#d6d6d1;font:700 10px/1 sans-serif}.piece-level input{width:100%;accent-color:#d7ff3f}.piece-level output{color:#9b9ba1;font:700 9px/1 monospace;text-align:right}.piece-level button{border:1px solid #414146;border-radius:5px;background:#242427;color:#aaa;padding:4px 0;cursor:pointer;font:700 9px/1 monospace}.piece-level button:hover,.piece-level button:focus-visible{border-color:#d7ff3f;color:#fff;outline:none}.play-along{margin:0 0 18px;padding:16px;border:1px solid #303035;border-radius:10px;background:linear-gradient(135deg,#18181b,#111113)}.play-along-head{display:flex;align-items:baseline;justify-content:space-between;gap:12px;margin-bottom:12px}.play-along-head h3{margin:0;color:#f2f2ed;font-size:15px}.play-along-head span{color:#d7ff3f;font:700 9px/1 monospace;text-transform:uppercase}.riff-picker{display:flex;gap:7px;flex-wrap:wrap}.riff-picker button{border:1px solid #3b3b40;border-radius:999px;background:#202024;color:#b8b8bd;padding:8px 11px;cursor:pointer;font:700 10px/1 sans-serif}.riff-picker button:hover,.riff-picker button:focus-visible{border-color:#d7ff3f;outline:none}.riff-picker button.active{border-color:#d7ff3f;background:#d7ff3f;color:#101012}.riff-summary{margin:12px 0 14px;color:#a7a7ac;font-size:11px;line-height:1.5}.riff-summary b{color:#e7e7e2}.band-mixer{display:flex;gap:12px;flex-wrap:wrap}.backing-channel{display:grid;grid-template-columns:auto minmax(100px,180px) 38px;align-items:center;gap:8px;min-width:260px}.backing-channel button{min-width:65px;border:1px solid #4a4a50;border-radius:6px;background:#252529;color:#e0e0dc;padding:7px 8px;cursor:pointer;font:700 10px/1 sans-serif}.backing-channel button.muted{color:#777;text-decoration:line-through}.backing-channel input{width:100%;accent-color:#d7ff3f}.backing-channel output{color:#9b9ba1;font:700 9px/1 monospace;text-align:right}.explanation{min-height:52px;margin:0 0 18px;padding:14px 17px;border-left:3px solid #d7ff3f;background:#171719;color:#aaa;font-size:12px;line-height:1.55}.scroll{overflow-x:auto;padding-bottom:8px}.grid{display:grid;grid-template-columns:90px repeat(64,minmax(13px,1fr));gap:3px;min-width:1420px}.ruler-title{color:#d7ff3f;font:700 9px/18px monospace;text-transform:uppercase}.step-marker{height:18px;color:#777;font:700 8px/18px monospace;text-align:left}.step-marker.beat-start{border-left:2px solid #777;padding-left:3px}.step-marker.bar-start{border-left:4px solid #d7ff3f;color:#d7ff3f;padding-left:4px;white-space:nowrap}.row-toggle{height:25px;border:1px solid #3a3a3e;border-radius:5px;background:#1c1c1f;color:#bbb;text-align:left;padding:0 8px;font:700 10px/23px sans-serif;cursor:pointer}.row-toggle.muted{text-decoration:line-through;color:#666}.cell{box-sizing:border-box;padding:0;height:25px;border:0;border-radius:3px;background:#29292c;min-width:13px;cursor:pointer}.cell.beat-start{box-shadow:inset 2px 0 #85858a}.cell.bar-start{box-shadow:inset 4px 0 #d7ff3f}.cell.hit{background:color-mix(in srgb,var(--hit-color) var(--strength),#29292c)}.cell.soft-hit{--strength:24%}.cell.normal-hit{--strength:58%}.cell.strong-hit{--strength:100%;border:1px solid #fff}.cell.hit[data-row="kick"]{--hit-color:#d7ff3f}.cell.hit[data-row="snare"]{--hit-color:#ff725e}.cell.hit[data-row="hat"]{--hit-color:#f1d06a}.cell.hit[data-row="rackTom"]{--hit-color:#8c77ff}.cell.hit[data-row="floorTom"]{--hit-color:#4c8eff}.cell.current{outline:2px solid white;outline-offset:1px}.legend{display:flex;gap:15px;flex-wrap:wrap;margin:12px 0 0 93px;color:#777;font-size:10px}.legend span{display:flex;align-items:center;gap:5px}.legend i{box-sizing:border-box;width:9px;height:9px;border-radius:2px;background:#d7ff3f}.legend .soft{background:color-mix(in srgb,#d7ff3f 24%,#29292c)}.legend .normal{background:color-mix(in srgb,#d7ff3f 58%,#29292c)}.legend .strong{border:1px solid #fff}.hint{color:#85858a;font-size:11px;margin:11px 0 0 93px}@media(max-width:650px){.tempo{margin-left:0}.grid{min-width:1420px}.legend,.hint{margin-left:0}.play-along-head,.kit-balance-head{align-items:flex-start;flex-direction:column}.backing-channel{grid-template-columns:65px minmax(90px,1fr) 36px;min-width:100%;flex:1}.piece-levels{grid-template-columns:1fr}}
    .cell.hit[data-row="hiHat"]{--hit-color:#f1d06a}.cell.hit[data-row="ride"]{--hit-color:#66c9c5}.cell.hit[data-row="crash"]{--hit-color:#ff9e57}
    .legend-row{display:flex;align-items:flex-start;gap:12px}.grid-help{position:relative;margin:7px 0 0 auto}.grid-help>button{width:24px;height:24px;border:1px solid #46464b;border-radius:50%;background:#1c1c1f;color:#aaa;cursor:help;font:700 11px/1 monospace}.grid-help>button:hover,.grid-help>button:focus-visible{border-color:#d7ff3f;color:#d7ff3f;outline:none}.grid-help-copy{position:absolute;right:0;bottom:31px;z-index:3;width:min(310px,75vw);padding:10px 12px;border:1px solid #4a4a50;border-radius:7px;background:#101012;color:#c7c7c3;box-shadow:0 8px 28px #000b;font:500 10px/1.5 sans-serif;opacity:0;visibility:hidden;transform:translateY(4px);transition:opacity .12s,transform .12s,visibility .12s}.grid-help:hover .grid-help-copy,.grid-help:focus-within .grid-help-copy{opacity:1;visibility:visible;transform:none}@media(max-width:650px){.legend-row{align-items:center}.grid-help{margin-top:7px}}
  `;

  willUpdate(changed: Map<PropertyKey, unknown>): void {
    if (changed.has("concept")) this.resetForConcept();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stop();
    this.metalKit.destroy();
  }

  private resetForConcept(): void {
    this.pattern = basePattern();
    this.swing = false;
    this.muted = unmutedRows();
    this.selectedPreset = "";
    this.explanation = resetCopy[this.concept] ?? resetCopy["build-a-groove"];
  }

  private cycle(row: DrumRow, index: number): void {
    const levels = [0, 0.32, 0.72, 1];
    const current = this.pattern[row][index];
    const next = levels[(levels.findIndex((level) => Math.abs(level - current) < 0.02) + 1) % levels.length];
    this.pattern = { ...this.pattern, [row]: this.pattern[row].map((level, step) => step === index ? next : level) };
  }

  private applyPreset(preset: Preset): void {
    this.selectedPreset = preset.id;
    this.explanation = preset.explanation;
    this.swing = preset.id === "swing";
    if (preset.id === "fill") {
      const next = clonePattern(this.pattern);
      for (let index = 60; index < 64; index += 1) {
        next.hiHat[index] = 0;
        next.ride[index] = 0;
        next.crash[index] = 0;
      }
      next.rackTom[60] = 0.72;
      next.rackTom[61] = 0.9;
      next.floorTom[62] = 0.82;
      next.floorTom[63] = 1;
      this.pattern = next;
      return;
    }
    if (preset.id === "ghosts") {
      const next = clonePattern(this.pattern);
      for (let index = 2; index < STEP_COUNT; index += 4) if (!next.snare[index]) next.snare[index] = 0.32;
      this.pattern = next;
      return;
    }
    if (preset.id === "accents") {
      const next = clonePattern(this.pattern);
      next.hiHat = Array.from({ length: STEP_COUNT }, (_, index) => index % 4 !== 0 && index % 2 === 0 ? 0.32 : 0);
      next.ride = steps();
      next.crash = Array.from({ length: STEP_COUNT }, (_, index) => index % 4 === 0 ? 1 : 0);
      this.pattern = next;
      return;
    }
    const next = basePattern();
    if (preset.id === "backbeat") this.pattern = next;
    if (preset.id === "kick-answer") { next.kick = hits([0, 3, 6, 10, 16, 19, 22, 26], 0.9); this.pattern = next; }
    if (preset.id === "half-time") { next.snare = hits([8, 24], 1); this.pattern = next; }
    if (preset.id === "double-time") { next.snare = hits([2, 6, 10, 14, 18, 22, 26, 30], 0.82); this.pattern = next; }
    if (preset.id === "gallop") { next.kick = Array.from({ length: STEP_COUNT }, (_, index) => [0, 2, 3].includes(index % 8) ? 0.9 : 0); this.pattern = next; }
    if (preset.id === "double-kick") { next.kick = Array(STEP_COUNT).fill(0.78); this.pattern = next; }
    if (preset.id === "riff-sync") { next.kick = hits([0, 3, 6, 10, 16, 19, 22, 26], 0.95); this.pattern = next; }
    if (preset.id === "space") { next.hiHat = steps(); next.crash = hits([0, 8, 16, 24], 0.8); next.snare = hits([8, 24], 1); next.kick = hits([0, 16], 1); this.pattern = next; }
    if (preset.id === "swing") { next.hiHat = steps(); next.ride = Array.from({ length: STEP_COUNT }, (_, index) => index % 2 === 0 ? 0.82 : 0.34); this.pattern = next; }
    if (preset.id === "d-beat") { next.hiHat = hits([...Array(16)].map((_, i) => i * 2), 0.72); next.snare = hits([4, 10, 12, 20, 26, 28], 0.9); next.kick = hits([0, 6, 8, 16, 22, 24], 0.9); this.pattern = next; }
    if (preset.id === "skank") { next.hiHat = hits([...Array(16)].map((_, i) => i * 2), 0.7); next.kick = hits([0, 4, 8, 12, 16, 20, 24, 28], 0.86); next.snare = hits([2, 6, 10, 14, 18, 22, 26, 30], 0.86); this.pattern = next; }
    if (preset.id === "traditional-blast") { next.hiHat = hits([...Array(16)].map((_, i) => i * 2), 0.62); next.kick = [...next.hiHat]; next.snare = hits([...Array(16)].map((_, i) => i * 2 + 1), 0.78); this.pattern = next; }
    if (preset.id === "hammer-blast") { next.hiHat = hits([...Array(16)].map((_, i) => i * 2), 0.58); next.kick = [...next.hiHat]; next.snare = hits([...Array(16)].map((_, i) => i * 2), 0.78); this.pattern = next; }
    if (preset.id === "bomb-blast") { next.hiHat = hits([...Array(16)].map((_, i) => i * 2), 0.56); next.snare = hits([2, 6, 10, 14, 18, 22, 26, 30], 0.76); next.kick = Array(STEP_COUNT).fill(0.72); this.pattern = next; }
    if (preset.id === "hyperblast") { next.hiHat = steps(); next.ride = hits([0, 8, 16, 24], 0.75); next.kick = hits([0, 8, 16, 24], 0.9); next.snare = Array(STEP_COUNT).fill(0.55); this.pattern = next; }
    if (preset.id === "breakdown") { next.hiHat = steps(); next.crash = hits([0, 3, 8, 12, 16, 19, 24, 28], 0.8); next.kick = [...next.crash]; next.snare = hits([8, 24], 1); this.pattern = next; }
    if (preset.id === "odd-grouping") { next.kick = Array.from({ length: STEP_COUNT }, (_, index) => index % 3 === 0 ? 0.86 : 0); this.pattern = next; }
  }

  private async start(): Promise<void> {
    if (this.playing) { this.stop(); return; }
    this.kitLoading = true;
    this.audioError = "";
    try {
      await this.metalKit.ready(this.kitManifest, this.backingManifest);
      for (const row of rows) {
        this.metalKit.setDrumVoiceLevelDb(row.id, this.drumLevels[row.id]);
      }
      for (const instrument of ["guitar", "bass"] as const) {
        this.metalKit.setBackingEnabled(
          instrument,
          this.backingEnabled[instrument],
        );
        this.metalKit.setBackingLevelDb(
          instrument,
          this.backingLevels[instrument],
        );
      }
      const startDelay = await this.metalKit.startBacking(
        this.selectedBacking,
        String(this.tempo) as Tempo,
      );
      this.playing = true;
      this.step = -1;
      this.nextStepAtMs = performance.now() + startDelay;
      this.timer = window.setTimeout(() => this.advance(), startDelay);
    } catch (error) {
      this.audioError = error instanceof Error
        ? error.message
        : "The drum kit and play-along could not load";
      return;
    } finally {
      this.kitLoading = false;
    }
  }

  private advance(): void {
    if (!this.playing) return;
    this.step = (this.step + 1) % STEP_COUNT;
    for (const row of rows) {
      const velocity = this.pattern[row.id][this.step];
      if (!this.muted[row.id] && velocity > 0) this.metalKit.hit(row.id, velocity);
    }
    const baseDelay = (60_000 / (110 * this.tempo)) / 4;
    const delay = this.swing ? baseDelay * (this.step % 2 === 0 ? 1.34 : 0.66) : baseDelay;
    // Advance against an absolute clock. Chaining relative setTimeout delays
    // would slowly move the editable drums away from the exact audio loop.
    this.nextStepAtMs = (this.nextStepAtMs ?? performance.now()) + delay;
    this.timer = window.setTimeout(
      () => this.advance(),
      Math.max(0, this.nextStepAtMs - performance.now()),
    );
  }

  private stop(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    this.nextStepAtMs = undefined;
    this.playing = false;
    this.step = -1;
    this.metalKit.stop();
  }

  private changeTempo(value: number): void {
    const wasPlaying = this.playing;
    this.stop();
    this.tempo = value;
    if (wasPlaying) void this.start();
  }

  private selectBacking(riff: string): void {
    const wasPlaying = this.playing;
    this.stop();
    this.selectedBacking = riff;
    if (wasPlaying) void this.start();
  }

  private toggleBacking(instrument: BackingInstrument): void {
    const enabled = !this.backingEnabled[instrument];
    this.backingEnabled = { ...this.backingEnabled, [instrument]: enabled };
    this.metalKit.setBackingEnabled(instrument, enabled);
  }

  private changeBackingLevel(
    instrument: BackingInstrument,
    event: Event,
  ): void {
    const level = Number((event.currentTarget as HTMLInputElement).value);
    this.backingLevels = { ...this.backingLevels, [instrument]: level };
    this.metalKit.setBackingLevelDb(instrument, level);
  }

  private setDrumLevel(row: DrumRow, levelDb: number): void {
    const bounded = Math.max(-12, Math.min(6, levelDb));
    this.drumLevels = { ...this.drumLevels, [row]: bounded };
    this.metalKit.setDrumVoiceLevelDb(row, bounded);
  }

  render() {
    const available = presets[this.concept] ?? presets["build-a-groove"];
    const backing = backingChoices.find(
      (choice) => choice.id === this.selectedBacking,
    ) ?? backingChoices[0];
    return html`
      <div class="toolbar">
        <button class="primary" ?disabled=${this.kitLoading} @click=${this.start}>${this.playing ? "Stop" : this.kitLoading ? "Loading kit…" : "Play groove"}</button>
        ${available.map((preset) => html`<button class="${this.selectedPreset === preset.id ? "active" : ""} ${preset.id === "fill" || preset.id === "ghosts" || preset.id === "accents" ? "additive" : ""}" @click=${() => this.applyPreset(preset)}>${preset.label}</button>`)}
        <button @click=${() => this.resetForConcept()}>Reset</button>
        <div class="tempo" aria-label="Tempo">${[[0.5, "½×"], [1, "1×"], [1.2, "1.2×"]].map(([value, label]) => html`<button class=${this.tempo === value ? "active" : ""} @click=${() => this.changeTempo(value as number)}>${label}</button>`)}</div>
      </div>
      ${this.audioError ? html`<p class="kit-note error">${this.audioError}</p>` : ""}
      <section class="kit-balance" aria-label="Drum piece loudness">
        <div class="kit-balance-head">
          <h3>Balance the kit</h3>
          <span>Each piece has its own loudness</span>
        </div>
        <div class="piece-levels">
          ${rows.map((row) => html`
            <div class="piece-level">
              <label for="${row.id}-level">${row.label}</label>
              <input
                id="${row.id}-level"
                data-drum-level=${row.id}
                type="range"
                min="-12"
                max="6"
                step="1"
                .value=${String(this.drumLevels[row.id])}
                aria-label="${row.label} volume"
                @input=${(event: Event) => this.setDrumLevel(row.id, Number((event.currentTarget as HTMLInputElement).value))}
              >
              <output>${this.drumLevels[row.id] > 0 ? "+" : ""}${this.drumLevels[row.id]} dB</output>
              <button type="button" title="Reset ${row.label} volume" aria-label="Reset ${row.label} volume" @click=${() => this.setDrumLevel(row.id, defaultDrumLevels()[row.id])}>↺</button>
            </div>
          `)}
        </div>
      </section>
      <section class="play-along" aria-label="Play-along riff">
        <div class="play-along-head">
          <h3>Give the groove a riff</h3>
          <span>Original Ghostbeats guitar + bass</span>
        </div>
        <div class="riff-picker">
          ${backingChoices.map((choice) => html`
            <button
              class=${this.selectedBacking === choice.id ? "active" : ""}
              aria-pressed=${this.selectedBacking === choice.id}
              ?disabled=${this.kitLoading}
              @click=${() => this.selectBacking(choice.id)}
            >${choice.label}</button>
          `)}
        </div>
        <p class="riff-summary">
          <b>${backing.label}.</b> ${backing.description}
          ${backing.id ? html`Try it with: ${backing.worksWith}.` : ""}
        </p>
        ${backing.id ? html`
          <div class="band-mixer">
            ${(["guitar", "bass"] as const).map((instrument) => html`
              <div class="backing-channel">
                <button
                  type="button"
                  class=${this.backingEnabled[instrument] ? "" : "muted"}
                  aria-pressed=${this.backingEnabled[instrument]}
                  @click=${() => this.toggleBacking(instrument)}
                >${this.backingEnabled[instrument] ? "Mute" : "Unmute"} ${instrument}</button>
                <input
                  type="range"
                  min="-12"
                  max="6"
                  step="0.5"
                  aria-label="${instrument} volume"
                  .value=${String(this.backingLevels[instrument])}
                  @input=${(event: Event) => this.changeBackingLevel(instrument, event)}
                >
                <output>${this.backingLevels[instrument] > 0 ? "+" : ""}${this.backingLevels[instrument]} dB</output>
              </div>
            `)}
          </div>
        ` : ""}
      </section>
      <p class="explanation" aria-live="polite">${this.explanation}</p>
      <div class="scroll">
        <div class="grid" aria-label="Editable four-bar drum sequencer">
          <span class="ruler-title">4 bars</span>
          ${Array.from({ length: STEP_COUNT }, (_, index) => {
            const barStart = index > 0 && index % STEPS_PER_BAR === 0;
            const beat = (index % STEPS_PER_BAR) / 4 + 1;
            return html`<span class="step-marker ${index % 4 === 0 ? "beat-start" : ""} ${barStart ? "bar-start" : ""}">${index % STEPS_PER_BAR === 0 ? `BAR ${Math.floor(index / STEPS_PER_BAR) + 1} · 1` : index % 4 === 0 ? beat : ""}</span>`;
          })}
          ${rows.map((row) => html`
            <button class="row-toggle ${this.muted[row.id] ? "muted" : ""}" @click=${() => { this.muted = { ...this.muted, [row.id]: !this.muted[row.id] }; }}>${this.muted[row.id] ? "Unmute" : "Mute"} ${row.label}</button>
            ${this.pattern[row.id].map((level, index) => html`<button aria-label="${row.label}, step ${index + 1}, ${level === 0 ? "off" : level < 0.5 ? "soft" : level < 0.9 ? "normal" : "accent"}" data-row=${row.id} class="cell ${strengthClass(level)} ${this.step === index ? "current" : ""} ${index % 4 === 0 ? "beat-start" : ""} ${index > 0 && index % STEPS_PER_BAR === 0 ? "bar-start" : ""}" @click=${() => this.cycle(row.id, index)}></button>`)}
          `)}
        </div>
      </div>
      <div class="legend-row">
        <div class="legend"><span><i class="soft"></i>soft / ghost</span><span><i class="normal"></i>normal</span><span><i class="strong"></i>strong accent · white rim</span></div>
        <span class="grid-help">
          <button type="button" aria-label="How to use the drum grid" aria-describedby="grid-help-copy">?</button>
          <span id="grid-help-copy" class="grid-help-copy" role="tooltip">Four bars, with four beats in each bar. Gray lines mark beats; bright lines start a new bar. Select a row name to mute that piece, or select a cell repeatedly to cycle soft → normal → strong → off.</span>
        </span>
      </div>
    `;
  }
}
